/**
 * Formulaire d’inscription concours — structure officielle (15 rubriques).
 * Les réponses sont stockées dans contest_registrations.form_data (JSONB).
 */

export type ContestFieldType =
  | 'text'
  | 'email'
  | 'tel'
  | 'date'
  | 'number'
  | 'textarea'
  | 'select'
  | 'checkbox'
  | 'multicheck'
  | 'info';

export interface ContestFieldOption {
  value: string;
  label: string;
}

export interface ContestFormField {
  key: string;
  label: string;
  type: ContestFieldType;
  required?: boolean;
  placeholder?: string;
  hint?: string;
  options?: ContestFieldOption[];
  /** Pour textarea : nombre de lignes */
  rows?: number;
  /** Validation mots (textarea) */
  minWords?: number;
  maxWords?: number;
  /** Affiché uniquement si un autre champ a une certaine valeur */
  showWhen?: { field: string; equals: string };
}

export interface ContestFormSection {
  key: string;
  number: number;
  label: string;
  description?: string;
  fields: ContestFormField[];
}

export const CONTEST_SEXE_OPTIONS: ContestFieldOption[] = [
  { value: 'homme', label: 'Homme' },
  { value: 'femme', label: 'Femme' },
  { value: 'autre', label: 'Autre' },
];

export const CONTEST_STATUT_PRO_OPTIONS: ContestFieldOption[] = [
  { value: 'etudiant', label: 'Étudiant' },
  { value: 'salarie', label: 'Salarié' },
  { value: 'entrepreneur', label: 'Entrepreneur' },
  { value: 'demandeur_emploi', label: 'Demandeur d’emploi' },
  { value: 'autre', label: 'Autre' },
];

export const CONTEST_NIVEAU_ETUDES_OPTIONS: ContestFieldOption[] = [
  { value: 'primaire', label: 'Primaire' },
  { value: 'secondaire', label: 'Secondaire' },
  { value: 'bac', label: 'Baccalauréat' },
  { value: 'licence', label: 'Licence / Bachelor' },
  { value: 'master', label: 'Master' },
  { value: 'doctorat', label: 'Doctorat' },
  { value: 'autre', label: 'Autre' },
];

export const CONTEST_STATUT_JURIDIQUE_OPTIONS: ContestFieldOption[] = [
  { value: 'aucune', label: 'Pas encore d’entreprise' },
  { value: 'ei', label: 'Entreprise individuelle' },
  { value: 'sarl', label: 'SARL' },
  { value: 'sa', label: 'SA' },
  { value: 'sas', label: 'SAS' },
  { value: 'association', label: 'Association' },
  { value: 'cooperative', label: 'Coopérative' },
  { value: 'autre', label: 'Autre' },
];

export const CONTEST_SECTEUR_OPTIONS: ContestFieldOption[] = [
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'industrie', label: 'Industrie' },
  { value: 'commerce', label: 'Commerce' },
  { value: 'services', label: 'Services' },
  { value: 'tic', label: 'TIC' },
  { value: 'sante', label: 'Santé' },
  { value: 'education', label: 'Éducation' },
  { value: 'transport', label: 'Transport' },
  { value: 'tourisme', label: 'Tourisme' },
  { value: 'energie', label: 'Énergie' },
  { value: 'artisanat', label: 'Artisanat' },
  { value: 'fintech', label: 'FinTech' },
  { value: 'environnement', label: 'Environnement' },
  { value: 'autre', label: 'Autre' },
];

export const CONTEST_NIVEAU_AVANCEMENT_OPTIONS: ContestFieldOption[] = [
  { value: 'idee', label: 'Idée' },
  { value: 'prototype', label: 'Prototype' },
  { value: 'test', label: 'Test' },
  { value: 'premiere_vente', label: 'Première vente' },
  { value: 'entreprise_activite', label: 'Entreprise en activité' },
];

export const CONTEST_UTILISATION_FONDS_OPTIONS: ContestFieldOption[] = [
  { value: 'equipements', label: 'Équipements' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'recrutement', label: 'Recrutement' },
  { value: 'fonds_roulement', label: 'Fonds de roulement' },
  { value: 'rd', label: 'R&D / innovation' },
  { value: 'autre', label: 'Autre' },
];

export const CONTEST_SOURCE_OPTIONS: ContestFieldOption[] = [
  { value: 'reseaux_sociaux', label: 'Réseaux sociaux' },
  { value: 'site_web', label: 'Site web ISBD' },
  { value: 'bouche_oreille', label: 'Bouche-à-oreille' },
  { value: 'structure_appui', label: 'Structure d’appui' },
  { value: 'media', label: 'Médias' },
  { value: 'evenement', label: 'Événement / salon' },
  { value: 'autre', label: 'Autre' },
];

export const CONTEST_FORM_SECTIONS: ContestFormSection[] = [
  {
    key: 'personnel',
    number: 1,
    label: 'Informations personnelles',
    description: 'Identité et coordonnées du candidat.',
    fields: [
      { key: 'nom_prenom', label: 'Nom et prénom', type: 'text', required: true },
      { key: 'sexe', label: 'Sexe', type: 'select', required: true, options: CONTEST_SEXE_OPTIONS },
      { key: 'date_naissance', label: 'Date de naissance', type: 'date', required: true },
      { key: 'nationalite', label: 'Nationalité', type: 'text', required: true },
      { key: 'telephone', label: 'Téléphone', type: 'tel', required: true },
      { key: 'whatsapp', label: 'WhatsApp', type: 'tel', required: true },
      { key: 'email', label: 'E-mail', type: 'email', required: true },
      { key: 'ville', label: 'Ville', type: 'text', required: true },
      { key: 'commune', label: 'Commune', type: 'text', required: true },
      { key: 'adresse', label: 'Adresse', type: 'textarea', required: true, rows: 2 },
    ],
  },
  {
    key: 'professionnel',
    number: 2,
    label: 'Situation professionnelle',
    description: 'Votre parcours et statut actuel.',
    fields: [
      {
        key: 'niveau_etudes',
        label: 'Niveau d’études',
        type: 'select',
        required: true,
        options: CONTEST_NIVEAU_ETUDES_OPTIONS,
      },
      { key: 'profession_actuelle', label: 'Profession actuelle', type: 'text', required: true },
      {
        key: 'statut_pro',
        label: 'Statut',
        type: 'select',
        required: true,
        options: CONTEST_STATUT_PRO_OPTIONS,
      },
      {
        key: 'experience_professionnelle',
        label: 'Expérience professionnelle',
        type: 'textarea',
        required: true,
        rows: 4,
        placeholder: 'Décrivez brièvement votre parcours…',
      },
    ],
  },
  {
    key: 'entreprise',
    number: 3,
    label: 'Informations sur l’entreprise',
    description: 'À renseigner si une structure existe déjà (sinon laissez vide ou indiquez « Pas encore »).',
    fields: [
      {
        key: 'nom_entreprise',
        label: 'Nom de l’entreprise',
        type: 'text',
        hint: 'Si une entreprise existe déjà',
      },
      { key: 'annee_creation', label: 'Année de création', type: 'number', placeholder: 'Ex. 2022' },
      {
        key: 'statut_juridique',
        label: 'Statut juridique',
        type: 'select',
        options: CONTEST_STATUT_JURIDIQUE_OPTIONS,
      },
      { key: 'rccm', label: 'RCCM', type: 'text', hint: 'Si disponible' },
      {
        key: 'secteur_entreprise',
        label: 'Secteur d’activité de l’entreprise',
        type: 'select',
        options: CONTEST_SECTEUR_OPTIONS,
      },
      { key: 'nombre_employes', label: 'Nombre d’employés', type: 'text', placeholder: 'Ex. 0, 1–5, 6–10…' },
      {
        key: 'chiffre_affaires',
        label: 'Chiffre d’affaires',
        type: 'text',
        hint: 'Facultatif',
        placeholder: 'Montant approximatif / année',
      },
    ],
  },
  {
    key: 'projet',
    number: 4,
    label: 'Présentation du projet',
    description: 'Décrivez clairement votre projet entrepreneurial.',
    fields: [
      { key: 'titre_projet', label: 'Titre du projet', type: 'text', required: true },
      {
        key: 'resume_projet',
        label: 'Résumé du projet',
        type: 'textarea',
        required: true,
        rows: 8,
        minWords: 300,
        maxWords: 500,
        hint: 'Entre 300 et 500 mots',
        placeholder: 'Présentez votre projet de façon structurée…',
      },
      {
        key: 'probleme_identifie',
        label: 'Problème identifié',
        type: 'textarea',
        required: true,
        rows: 4,
      },
      {
        key: 'solution_proposee',
        label: 'Solution proposée',
        type: 'textarea',
        required: true,
        rows: 4,
      },
    ],
  },
  {
    key: 'secteur',
    number: 5,
    label: 'Secteur d’activité',
    description: 'Secteur principal du projet présenté.',
    fields: [
      {
        key: 'secteur_activite',
        label: 'Secteur d’activité',
        type: 'select',
        required: true,
        options: CONTEST_SECTEUR_OPTIONS,
      },
      {
        key: 'secteur_autre',
        label: 'Précisez le secteur',
        type: 'text',
        showWhen: { field: 'secteur_activite', equals: 'autre' },
      },
    ],
  },
  {
    key: 'innovation',
    number: 6,
    label: 'Innovation',
    description: 'Ce qui rend votre projet innovant et unique.',
    fields: [
      {
        key: 'innovation_description',
        label: 'En quoi votre projet est-il innovant ?',
        type: 'textarea',
        required: true,
        rows: 4,
      },
      {
        key: 'differenciation',
        label: 'Qu’est-ce qui le différencie des solutions existantes ?',
        type: 'textarea',
        required: true,
        rows: 4,
      },
    ],
  },
  {
    key: 'marche',
    number: 7,
    label: 'Marché',
    description: 'Clients, taille du marché et concurrence.',
    fields: [
      {
        key: 'clients_cibles',
        label: 'Qui sont vos clients ?',
        type: 'textarea',
        required: true,
        rows: 3,
      },
      {
        key: 'taille_marche',
        label: 'Taille du marché',
        type: 'textarea',
        required: true,
        rows: 3,
      },
      {
        key: 'concurrents',
        label: 'Principaux concurrents',
        type: 'textarea',
        required: true,
        rows: 3,
      },
    ],
  },
  {
    key: 'avancement',
    number: 8,
    label: 'Niveau d’avancement',
    fields: [
      {
        key: 'niveau_avancement',
        label: 'Niveau d’avancement',
        type: 'select',
        required: true,
        options: CONTEST_NIVEAU_AVANCEMENT_OPTIONS,
      },
    ],
  },
  {
    key: 'modele_economique',
    number: 9,
    label: 'Modèle économique',
    fields: [
      {
        key: 'modele_economique',
        label: 'Comment votre projet génère-t-il des revenus ?',
        type: 'textarea',
        required: true,
        rows: 4,
      },
    ],
  },
  {
    key: 'impact',
    number: 10,
    label: 'Impact',
    description: 'Emplois et impacts social, environnemental et local.',
    fields: [
      { key: 'emplois_prevus', label: 'Emplois prévus', type: 'text', required: true, placeholder: 'Ex. 5 emplois à 12 mois' },
      { key: 'impact_social', label: 'Impact social', type: 'textarea', required: true, rows: 3 },
      {
        key: 'impact_environnemental',
        label: 'Impact environnemental',
        type: 'textarea',
        required: true,
        rows: 3,
      },
      {
        key: 'contribution_locale',
        label: 'Contribution au développement local',
        type: 'textarea',
        required: true,
        rows: 3,
      },
    ],
  },
  {
    key: 'financement',
    number: 11,
    label: 'Besoin de financement',
    fields: [
      {
        key: 'montant_recherche',
        label: 'Montant recherché',
        type: 'text',
        required: true,
        placeholder: 'Ex. 5 000 000 FCFA',
      },
      {
        key: 'utilisation_fonds',
        label: 'Utilisation prévue des fonds',
        type: 'multicheck',
        required: true,
        options: CONTEST_UTILISATION_FONDS_OPTIONS,
      },
      {
        key: 'utilisation_fonds_detail',
        label: 'Précisions sur l’utilisation des fonds',
        type: 'textarea',
        rows: 3,
        placeholder: 'Détaillez la répartition si besoin…',
      },
    ],
  },
  {
    key: 'equipe',
    number: 12,
    label: 'Équipe',
    fields: [
      { key: 'nombre_membres', label: 'Nombre de membres', type: 'number', required: true },
      {
        key: 'competences_principales',
        label: 'Compétences principales',
        type: 'textarea',
        required: true,
        rows: 3,
      },
      {
        key: 'presentation_promoteur',
        label: 'Présentation du promoteur',
        type: 'textarea',
        required: true,
        rows: 4,
      },
    ],
  },
  {
    key: 'documents',
    number: 13,
    label: 'Documents',
    description:
      'Après l’envoi de votre candidature, vous pourrez déposer les pièces demandées (CV, business plan, pièce d’identité, etc.).',
    fields: [
      {
        key: 'documents_info',
        label: 'Pièces à préparer',
        type: 'info',
        hint: 'CV · Business plan / modèle · Pièce d’identité · RCCM (si entreprise) · Photos du produit · Vidéo de présentation (optionnelle)',
      },
    ],
  },
  {
    key: 'references',
    number: 14,
    label: 'Références',
    fields: [
      {
        key: 'source_connaissance',
        label: 'Comment avez-vous connu le concours ?',
        type: 'select',
        required: true,
        options: CONTEST_SOURCE_OPTIONS,
      },
      {
        key: 'accompagne_structure',
        label: 'Êtes-vous accompagné par une structure d’appui ?',
        type: 'select',
        required: true,
        options: [
          { value: 'oui', label: 'Oui' },
          { value: 'non', label: 'Non' },
        ],
      },
      {
        key: 'structure_appui',
        label: 'Nom de la structure d’appui',
        type: 'text',
        showWhen: { field: 'accompagne_structure', equals: 'oui' },
        required: true,
      },
    ],
  },
  {
    key: 'engagement',
    number: 15,
    label: 'Engagement',
    description: 'Lecture et acceptation des engagements avant soumission.',
    fields: [
      {
        key: 'declaration_honneur',
        label: 'Je déclare sur l’honneur l’exactitude des informations fournies.',
        type: 'checkbox',
        required: true,
      },
      {
        key: 'acceptation_reglement',
        label: 'J’accepte le règlement du concours.',
        type: 'checkbox',
        required: true,
      },
      {
        key: 'autorisation_donnees',
        label: 'J’autorise le traitement de mes données dans le cadre du concours.',
        type: 'checkbox',
        required: true,
      },
    ],
  },
];

export const CONTEST_DOC_TYPES = [
  { key: 'cv_porteur', label: 'CV du porteur', required: true },
  { key: 'business_plan', label: 'Business plan / modèle économique', required: true },
  { key: 'piece_identite', label: 'Pièce d’identité', required: true },
  { key: 'rccm', label: 'RCCM (si entreprise)', required: false },
  { key: 'photos_produit', label: 'Photos du produit', required: false },
  { key: 'video_presentation', label: 'Vidéo de présentation', required: false },
] as const;

export type ContestFormData = Record<string, string>;

export function emptyContestFormData(): ContestFormData {
  const data: ContestFormData = {};
  for (const section of CONTEST_FORM_SECTIONS) {
    for (const field of section.fields) {
      if (field.type === 'info') continue;
      if (field.type === 'multicheck') data[field.key] = '';
      else if (field.type === 'checkbox') data[field.key] = 'false';
      else data[field.key] = '';
    }
  }
  return data;
}

export function contestFieldVisible(field: ContestFormField, data: ContestFormData): boolean {
  if (!field.showWhen) return true;
  return data[field.showWhen.field] === field.showWhen.equals;
}

export function countWords(text: string): number {
  return text
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

export function contestSectionErrors(
  section: ContestFormSection,
  data: ContestFormData,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of section.fields) {
    if (field.type === 'info') continue;
    if (!contestFieldVisible(field, data)) continue;

    const raw = data[field.key] ?? '';
    const value = raw.trim();

    if (field.required) {
      if (field.type === 'checkbox') {
        if (raw !== 'true') errors[field.key] = 'Engagement requis.';
      } else if (field.type === 'multicheck') {
        if (!value) errors[field.key] = 'Sélectionnez au moins une option.';
      } else if (!value) {
        errors[field.key] = 'Champ obligatoire.';
      }
    }

    if (field.type === 'textarea' && value && (field.minWords || field.maxWords)) {
      const words = countWords(value);
      if (field.minWords && words < field.minWords) {
        errors[field.key] = `Minimum ${field.minWords} mots (actuellement ${words}).`;
      } else if (field.maxWords && words > field.maxWords) {
        errors[field.key] = `Maximum ${field.maxWords} mots (actuellement ${words}).`;
      }
    }

    if (field.type === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      errors[field.key] = 'Adresse e-mail invalide.';
    }
  }
  return errors;
}

export function contestFormComplete(data: ContestFormData): boolean {
  return CONTEST_FORM_SECTIONS.every(
    (section) => Object.keys(contestSectionErrors(section, data)).length === 0,
  );
}

/** Libellé projet pour listes admin / candidat (compat anciennes clés). */
export function contestProjectTitle(data: ContestFormData | null | undefined): string {
  if (!data) return '';
  return data.titre_projet || data.nom_projet_entreprise || '';
}

/** Nom du candidat (compat anciennes clés). */
export function contestCandidateName(data: ContestFormData | null | undefined): string {
  if (!data) return '';
  return data.nom_prenom || data.nom_et_prenoms || '';
}

/** Enrichit form_data avec alias rétrocompatibles pour l’admin existant. */
export function withContestLegacyAliases(data: ContestFormData): ContestFormData {
  return {
    ...data,
    nom_et_prenoms: data.nom_prenom || data.nom_et_prenoms || '',
    nom_projet_entreprise: data.titre_projet || data.nom_projet_entreprise || '',
    probleme_identifie_besoin: data.probleme_identifie || data.probleme_identifie_besoin || '',
    solution_proposee_description: data.solution_proposee || data.solution_proposee_description || '',
  };
}

/** Affiche une valeur de champ lisible pour l’admin (résout select / checkbox / multicheck). */
export function formatContestFieldValue(
  field: ContestFormField,
  data: ContestFormData,
): string {
  const raw = (data[field.key] ?? '').trim();
  if (!raw && field.type !== 'checkbox') return '—';

  if (field.type === 'checkbox') {
    return raw === 'true' ? 'Oui' : 'Non';
  }

  if (field.type === 'select') {
    return field.options?.find((o) => o.value === raw)?.label ?? raw;
  }

  if (field.type === 'multicheck') {
    const values = raw.split(',').map((s) => s.trim()).filter(Boolean);
    if (!values.length) return '—';
    return values
      .map((v) => field.options?.find((o) => o.value === v)?.label ?? v)
      .join(', ');
  }

  if (field.type === 'date' && raw) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString('fr-FR');
    }
  }

  return raw;
}
